import i18next from "i18next"
import { initReactI18next } from "react-i18next"

import { logger } from "../utils/monitoring/logging"
import type { InstacarAppLanguage } from "../utils/types"
import csDefault from "./cs/default.json"

export const defaultNamespace = "default"
export const i18n = i18next.use(initReactI18next)

i18n.init({
  fallbackLng: "cs",
  defaultNS: defaultNamespace,
  partialBundledLanguages: true,
  // ensure some language always loads, for example for error page
  resources: { cs: { [defaultNamespace]: csDefault } },
  interpolation: {
    // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    escapeValue: false,
  },
  react: {
    useSuspense: true,
  },
})

async function loadBundles(language: string, organizationHumanReadableId?: string) {
  // when Vite loads json dynamically, it appends ?import to the file path
  const defaultLocalesModule = await import(`./${language}/default.json`)
  const defaultLocales = defaultLocalesModule.default || defaultLocalesModule

  // https://www.i18next.com/overview/api#addresourcebundle
  i18n.addResourceBundle(language, defaultNamespace, defaultLocales, true, true)

  if (organizationHumanReadableId !== undefined) {
    try {
      const organizationLocalesModule = await import(`./${language}/${organizationHumanReadableId}.json`)
      const organizationLocales = organizationLocalesModule.default || organizationLocalesModule
      i18n.addResourceBundle(language, defaultNamespace, organizationLocales, true, true)
    } catch {
      logger.debug(`No organization locales found for ${language} and ${organizationHumanReadableId}`)
    }
  }
}

// The session loader re-runs on every navigation, without this cache
// every step change would re-merge the whole bundle into the i18next store. Promises rather
// than flags, so parallel callers share one load instead of racing. Safe because the store is never
// cleared — nothing calls removeResourceBundle.
const bundleLoads = new Map<string, Promise<void>>()

function loadLocaleResources(language: string, organizationHumanReadableId?: string) {
  // Organization bundles merge into the same namespace, so the key has to cover both
  const key = `${language}|${organizationHumanReadableId ?? ""}`
  const pending = bundleLoads.get(key)

  if (pending) return pending

  const load = loadBundles(language, organizationHumanReadableId).catch((error: unknown) => {
    // Forget the failure so a later attempt retries again
    bundleLoads.delete(key)
    throw error
  })

  bundleLoads.set(key, load)

  return load
}

/**
 * pre-loads every offered language while the device is still online
 * without this, going offline after the session loaded would break language switching.
 */
export async function preloadLocaleResources(
  languages: readonly InstacarAppLanguage[],
  organizationHumanReadableId?: string,
) {
  // Settled, not all — a secondary language failing to load must not take the session down
  await Promise.allSettled(languages.map((language) => loadLocaleResources(language, organizationHumanReadableId)))
}

const HTML_LANG: Record<InstacarAppLanguage, string> = {
  cs: "cs",
  sk: "sk",
  en: "en",
  de: "de",
  es: "es",
  hu: "hu",
  pl: "pl",
  ka: "ka",
  fr: "fr",
  lb: "lb",
  pt: "pt",
}

/** Single entry point for switching the app language — loads resources and syncs the document. */
export async function applyLanguage(language: InstacarAppLanguage, organizationHumanReadableId?: string) {
  await loadLocaleResources(language, organizationHumanReadableId)

  if (i18n.language !== language) {
    await i18n.changeLanguage(language)
  }

  // index.html ships a static lang="en"; syncing it keeps screen readers, hyphenation and font
  // fallbacks on the language actually rendered
  document.documentElement.lang = HTML_LANG[language]
}
